import React from "react";
import {
  FormSection,
  InputField,
  FileUpload,
} from "../../../common/FormComponents";
import { MenuItem } from "@mui/material";
import TextField from "../../../common/TextField";
import { eventData } from "../../MyChamber/analyticsData";
import Tooltip from "../../../common/FormComponents/Tooltip";

type EventDetails = {
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  eventType: string;
  visibility: string;
  featuredImage: string;
  featuredImageFile: File | null;
};

type EventDetailsSectionProps = {
  eventDetails: EventDetails;
  onChange: (field: keyof EventDetails, value: string) => void;
  onFeaturedImageChange: (file: File | null) => void;
  errors?: Record<string, string>;
};

const EventDetailsSection = ({
  eventDetails,
  onChange,
  onFeaturedImageChange,
  errors = {},
}: EventDetailsSectionProps) => {
  const handleChange =
    (field: keyof EventDetails) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(field, e.target.value);
    };

  return (
    <FormSection
      title="Event Details"
      tooltipText="Basic information about your event"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <InputField
            id="event-title"
            name="title"
            label="Event Title"
            value={eventDetails.title}
            onChange={handleChange("title")}
            error={!!errors.title}
            helperText={errors.title || ""}
            required
            tooltipText="The name of your event as it will appear to attendees"
          />
        </div>

        <div>
          <div className="flex items-center mb-1">
            <label
              htmlFor="event-type"
              className="block text-sm font-medium text-gray-900"
            >
              Event Type
              <span className="text-red-500 ml-1">*</span>
            </label>
            <Tooltip content="The category or type of event" />
          </div>
          <TextField
            select
            fullWidth
            id="event-type"
            name="eventType"
            value={eventDetails.eventType}
            onChange={handleChange("eventType")}
            error={!!errors.eventType}
            helperText={errors.eventType || ""}
            required
            InputLabelProps={{ shrink: true }}
            label=""
          >
            {eventData.eventsByType.map((type) => (
              <MenuItem key={type.name} value={type.name}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div className="md:col-span-2">
          <InputField
            id="event-description"
            name="description"
            label="Event Description"
            value={eventDetails.description}
            onChange={handleChange("description")}
            error={!!errors.description}
            helperText={errors.description || ""}
            multiline
            rows={4}
            tooltipText="A detailed description of your event"
          />
        </div>

        <div>
          <TextField
            id="start-date"
            name="startDate"
            label="Start Date"
            type="date"
            value={eventDetails.startDate}
            onChange={handleChange("startDate")}
            error={!!errors.startDate}
            helperText={errors.startDate || ""}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <div>
          <TextField
            id="start-time"
            name="startTime"
            label="Start Time"
            type="time"
            value={eventDetails.startTime}
            onChange={handleChange("startTime")}
            error={!!errors.startTime}
            helperText={errors.startTime || ""}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <div>
          <TextField
            id="end-date"
            name="endDate"
            label="End Date"
            type="date"
            value={eventDetails.endDate}
            onChange={handleChange("endDate")}
            error={!!errors.endDate}
            helperText={errors.endDate || ""}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <div>
          <TextField
            id="end-time"
            name="endTime"
            label="End Time"
            type="time"
            value={eventDetails.endTime}
            onChange={handleChange("endTime")}
            error={!!errors.endTime}
            helperText={errors.endTime || ""}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <div>
          <TextField
            select
            fullWidth
            label="Visibility"
            value={eventDetails.visibility}
            onChange={handleChange("visibility")}
            error={!!errors.visibility}
            helperText={errors.visibility || ""}
            required
          >
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="private">Private</MenuItem>
            <MenuItem value="members">Members Only</MenuItem>
          </TextField>
        </div>

        <div className="md:col-span-2">
          <FileUpload
            id="featured-image"
            label="Featured Banner"
            onChange={onFeaturedImageChange}
            value={eventDetails.featuredImage}
            acceptedFileTypes="image/png,image/jpeg,image/jpg,image/gif"
            previewSize="large"
            helpText="Recommended size: 1200 x 630 pixels"
            tooltipText="A high-quality image that represents your event"
          />
        </div>
      </div>
    </FormSection>
  );
};

export default EventDetailsSection;
